import React from "react";
import { Typography } from "antd";
import type { ReactNode } from "react";

import { Value } from "@components/Text";
import { JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP } from "@components/Domain";

import breakRow from "./breakRow";
import renderAdditional from "./renderAdditional";
import renderResidenceState from "./renderResidenceState";
import type { DescriptionItem, Investor, ProfileJoint } from "./types";

const { Text } = Typography;

const LABEL_SSN = "Social Security Number";
const LABEL_TITLE = "Title of Membership Interests";
const LABEL_NAME1 = "Investor Legal Full Name";
const LABEL_NAME2 = "Co-Investor Legal Full Name";
const LABEL_EMAIL2 = "Co-Investor Email";
const LABEL_ADDRESS = "Address";
const LABEL_INVESTMENTS = "Investments";
const LABEL_NO_SIGNATURE = "Co-investor signature is not required.";

const NOTE_FONT_SIZE = 12;

const renderProfileJoint = (
  profileJoint: ProfileJoint,
  investmentsValue?: ReactNode,
  investor?: Investor,
  additionalExtra?: ReactNode,
): DescriptionItem[] => {
  const {
    membershipInterestsTitle,
    ssnNumber,
    jointEmail,
    jointAddress,
    jointGivenName,
    jointFamilyName,
    jointAdditionalName,
    shouldCoInvestorSign,
  } = profileJoint;

  const legalFullNameInvestor2 = [
    jointGivenName,
    jointAdditionalName,
    jointFamilyName,
  ].join(" ").trim();

  const items: DescriptionItem[] = [
    {
      label: LABEL_TITLE,
      value: JOINT_MEMBERSHIP_INTERESTS_TITLES_MAP[membershipInterestsTitle],
    },
  ];

  if (investmentsValue) {
    items.push({
      label: LABEL_INVESTMENTS,
      value: investmentsValue,
    });
  }

  if (jointAddress) {
    const residenceItems = renderResidenceState(
      profileJoint,
      jointAddress.addressCountry,
      jointAddress.addressRegion,
    );

    items.push(...residenceItems);
  }

  items.push({
    label: LABEL_SSN,
    value: <Value value={ssnNumber} />,
  });

  if (investor) {
    const {
      givenName,
      additionalName,
      familyName,
    } = investor;

    const legalFullNameInvestor1 = [
      givenName,
      additionalName,
      familyName,
    ].join(" ");

    items.push({
      label: LABEL_NAME1,
      value: legalFullNameInvestor1,
    });
  }

  items.push({
    label: LABEL_NAME2,
    value: legalFullNameInvestor2,
  });

  if (shouldCoInvestorSign) {
    items.push({
      label: LABEL_EMAIL2,
      value: jointEmail,
    });
  }

  items.push({
    label: LABEL_ADDRESS,
    value: <Value value={jointAddress} />,
  });

  const additionalValue = shouldCoInvestorSign
    ? additionalExtra
    : (
      <>
        <Text type="secondary" style={{ fontSize: NOTE_FONT_SIZE }}>
          {LABEL_NO_SIGNATURE}
        </Text>
        {additionalExtra}
      </>
    );

  breakRow(items);

  items.push(renderAdditional(profileJoint, additionalValue));

  return items;
};

export default renderProfileJoint;
